import { ShieldCheck, Sparkles, HeartHandshake, Award } from "lucide-react";
import { Reveal, PageHeader } from "@/components/Reveal";
import { CHAPTERS } from "@/lib/content";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

const IMG_INTERIOR =
  "/api/files/royal-skin-clinic/uploads/811c49a3-1169-4de0-8940-4d3b6c2c7f6b.webp";
const IMG_PRODUCTS =
  "/api/files/royal-skin-clinic/uploads/ac61203f-bae3-42c9-b448-bc913f35a778.webp";

const VALUES = [
  { icon: ShieldCheck, title: "Physician-Led", text: "Every protocol is designed and supervised by board-certified doctors. No compromises, ever." },
  { icon: Sparkles, title: "Artistic Precision", text: "We believe the best aesthetic work is invisible — you, simply more radiant." },
  { icon: HeartHandshake, title: "Concierge Care", text: "From your first consultation to your at-home ritual, a dedicated team walks beside you." },
  { icon: Award, title: "Proven Results", text: "15 years, 10,000+ patients and a 99.4% satisfaction rate. Our work speaks softly." },
];

export default function About() {
  const { content } = useContent();
  const { book } = useBooking();
  const s = content?.settings || {};

  return (
    <div className="px-4 pb-24 pt-44 sm:px-8 lg:px-16" data-testid="about-page">
      <PageHeader
        eyebrow="Our Story"
        title="A Sanctuary for Skin"
        subtitle="Royal Skin Cosmetic Clinic was founded on a simple belief: world-class dermatology should feel like a retreat, not a procedure."
        testid="about-title"
      />

      <div className="mx-auto mt-16 grid max-w-6xl items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <img src={IMG_INTERIOR} alt="Inside the Royal Skin Cosmetic Clinic luxury clinic" loading="lazy" className="aspect-[4/5] w-full rounded-[2.5rem] object-cover shadow-[0_20px_60px_rgba(26,22,21,0.15)]" />
            <img src={IMG_PRODUCTS} alt="Medical-grade skincare products" loading="lazy" className="glass absolute -bottom-8 -right-4 w-2/5 rounded-[1.5rem] border-4 border-[#FAF8F5] object-cover shadow-xl sm:-right-8" />
          </div>
        </Reveal>
        <div>
          <Reveal><p className="eyebrow">Since 2010</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-serif text-3xl font-medium leading-snug text-[#1A1615] sm:text-4xl">
              Where clinical excellence meets <span className="italic text-[#B38B2D]">quiet luxury</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-6 space-y-5 text-base font-light leading-relaxed text-[#5A504B]">
              <p>
                What began as a single treatment room has become one of the most trusted names in aesthetic medicine —
                a clinic where medical-grade technology is paired with the warmth of a private members' retreat.
              </p>
              <p>
                Our physicians spend as long as it takes to understand your skin's history, your lifestyle and your
                goals, before crafting a treatment journey that is entirely, unapologetically yours.
              </p>
              <p>
                Every detail — from the scent in the air to the thread count of our linens — exists so that the moment
                you walk through our doors, the world slows down.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <button onClick={() => book()} data-testid="about-book-button" className="btn-gold mt-8 px-8 py-4 text-sm">
              Experience It Yourself
            </button>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto mt-28 max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal><p className="eyebrow">Why Royal Skin</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-serif text-3xl font-medium text-[#1A1615] sm:text-4xl">The Difference Is in the Details</h2>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-values-grid">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div className="glass glass-card-hover h-full rounded-[2rem] p-8 text-center" data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37]/15">
                  <v.icon className="h-6 w-6 text-[#B38B2D]" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold text-[#1A1615]">{v.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#5A504B]">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-24 max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal><p className="eyebrow">Our Chapters</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-serif text-3xl font-medium text-[#1A1615] sm:text-4xl">The Story So Far</h2>
          </Reveal>
        </div>
        <div className="mt-12 space-y-6">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="glass glass-card-hover flex flex-col gap-3 rounded-[2rem] p-8 sm:flex-row sm:items-center sm:gap-10" data-testid={`about-chapter-${i}`}>
                <p className="w-32 shrink-0 font-serif text-lg italic text-[#B38B2D]">{c.n}</p>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-[#1A1615]">{c.title}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal className="mx-auto mt-24 max-w-6xl">
        <div className="glass flex flex-col items-center justify-between gap-8 rounded-[3rem] p-10 text-center sm:flex-row sm:text-left" data-testid="about-stats-band">
          {[
            { value: s.stat_years || "15+", label: "Years of Excellence" },
            { value: s.stat_patients || "10,000+", label: "Glowing Patients" },
            { value: s.stat_satisfaction || "99.4%", label: "Satisfaction Rate" },
            { value: "24/7", label: "Concierge Support" },
          ].map((st) => (
            <div key={st.label} className="flex-1">
              <p className="font-serif text-4xl font-semibold text-[#B38B2D]">{st.value}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#A39288]">{st.label}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  );
}
